import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Section } from "@/shared/components/ui/Section";
import { useSubscription, useSubscriptionCheckout, useSubscriptionPortal } from "../hooks/subscriptionQueries";
import { useOpenBillingUrl } from "../hooks/useOpenBillingUrl";
import type { SubscriptionSku } from "../types/subscriptionTypes";
import { CurrentPlanCard } from "./CurrentPlanCard";
import { PlanOffers } from "./PlanOffers";
import { SubscriptionHostRefresh } from "./SubscriptionHostRefresh";

interface PlanSettingsProps {
  businessId: string;
  userId: string;
}

export function PlanSettings({ businessId, userId }: PlanSettingsProps) {
  const query = useSubscription(businessId, userId);
  const checkout = useSubscriptionCheckout();
  const portal = useSubscriptionPortal();
  const billing = useOpenBillingUrl(userId);
  const subscription = query.data;

  const upgrade = (sku: SubscriptionSku) => void billing.open(() => checkout.mutateAsync({ businessId, sku }));
  const manage = () => void billing.open(() => portal.mutateAsync({ businessId }));

  return (
    <div className="space-y-6">
      <SubscriptionHostRefresh businessId={businessId} />
      {query.isPending ? (
        <LoadingSpinner message="Loading your plan..." />
      ) : query.error ? (
        <ErrorState
          title="Couldn't load your plan"
          message="Plan details are unavailable. Please try again."
          onRetry={() => void query.refetch()}
        />
      ) : subscription && (
        <>
          <CurrentPlanCard subscription={subscription} busy={billing.busy} onManage={manage} />
          {!subscription.sku && (
            <Section title="Upgrade">
              <p className="text-sm text-muted-foreground">Billed monthly by Stripe. Cancel anytime.</p>
              <PlanOffers offers={subscription.offers} busy={billing.busy} onUpgrade={upgrade} />
            </Section>
          )}
        </>
      )}
      {billing.message && <p role="status" className="text-sm leading-relaxed">{billing.message}</p>}
      <p className="text-xs leading-relaxed text-muted-foreground">
        Payments and invoices are handled securely by Stripe. Usage limits reset monthly.
      </p>
    </div>
  );
}
